import {
  ActiveOrganization,
  useUserRole,
  usePaymentAccount,
  useCreatePaymentAccount,
  useCreatePaymentAccountOnboardURL
} from "@/api";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { PaymentAccountStatus } from "@/gql/__generated__/graphql";
import { CheckCircle2, ExternalLink } from "lucide-react";
import RemovePaymentPlatfromConnection from "./RemovePaymentPlatfromConnection";

type Props = {
  organization: ActiveOrganization;
};

export const PaymentPlatformConnection = ({ organization }: Props) => {
  const { isAdmin } = useUserRole();

  const { data, loading: loadingPaymentAccount } = usePaymentAccount({
    organizationSlug: organization.slug
  });

  const [createPaymentAccount, { loading: creatingPaymentAccount }] =
    useCreatePaymentAccount({ organizationSlug: organization.slug });

  const { handleOpenPaymentOnboarding, loading: loadinOnboardURL } =
    useCreatePaymentAccountOnboardURL({
      organizationSlug: organization.slug
    });

  const loading =
    loadingPaymentAccount || creatingPaymentAccount || loadinOnboardURL;

  const hasPaymentAccount =
    data?.organization.__typename === "Organization" &&
    data.organization.paymentAccount !== null &&
    data.organization.paymentAccount?.id !== "";

  const accountStatus =
    data?.organization.__typename === "Organization" &&
    data.organization.paymentAccount?.status;

  if (loading) return <LoadingCircle />;
  if (!hasPaymentAccount)
    return (
      <Button
        onClick={() => createPaymentAccount().then(handleOpenPaymentOnboarding)}
        className="min-w-60 w-fit"
      >
        Connect payment account
        <ExternalLink size={14} className="ml-4" />
      </Button>
    );

  switch (accountStatus) {
    case PaymentAccountStatus.Restricted:
      return (
        <Button
          onClick={handleOpenPaymentOnboarding}
          className="min-w-60  w-fit"
        >
          Finish payment account
          <ExternalLink size={14} className="ml-4" />
        </Button>
      );
    case PaymentAccountStatus.Verified:
      return (
        <div className="flex flex-row items-end gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-400" />
            <Button variant="outline" onClick={handleOpenPaymentOnboarding}>
              Update
              <ExternalLink size={14} className="ml-4" />
            </Button>
          </div>
          {isAdmin && <RemovePaymentPlatfromConnection />}
        </div>
      );
    default:
      return (
        <Alert
          title="Unknown payment account status"
          description="Please try to reload the page or report the issue."
          variant="destructive"
        />
      );
  }
};
